import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { FSRConfigurationPermission } from '@/Interface/fsrManagementType';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';

/**
 * The FSR action matrix: one row per user type, one checkbox per action.
 *
 * Listing and updating only — rows are seeded one-per-UserType by migration
 * 0047, and the Customer type is excluded server-side.
 */
@Component({
  selector: 'app-fsr-configuration',
  templateUrl: './fsr-configuration.component.html',
  styleUrls: ['./fsr-configuration.component.scss'],
})
export class FsrConfigurationComponent implements OnInit {
  public userTypePermission: FSRConfigurationPermission[] = [];

  /** Column order in the table; also what onPermissionChange patches. */
  readonly permissionColumns: { key: keyof FSRConfigurationPermission; label: string }[] = [
    { key: 'assign', label: 'Assign' },
    { key: 'start', label: 'Start' },
    { key: 'on_hold', label: 'On Hold' },
    { key: 'pending_material', label: 'Pending Material' },
    { key: 'resolve', label: 'Resolve' },
    { key: 'close', label: 'Close' },
    { key: 're_open', label: 'Reopen' },
    { key: 'cancel', label: 'Cancel' },
    { key: 'approve', label: 'Approve' },
  ];

  searchElement = '';
  searchSubject: Subject<string> = new Subject();
  loader = false;
  tableLoader = false;

  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady = false;

  constructor(
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private permService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.initComponent();
  }

  async initComponent() {
    this.loader = true;
    try {
      await this.getComponentPermissions();
      this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
        this.searchElement = searchTerm;
        this.getTableData();
      });
      if (this.permissions.canView) {
        this.getTableData();
      }
    } finally {
      this.loader = false;
    }
  }

  async getComponentPermissions() {
    const url = this.permService.getCurrentUrl();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete,
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  private getTableData(): void {
    this.tableLoader = true;
    this.userTypePermission = [];
    this._utilityService.getFsrConfigurationList(this.searchElement).subscribe({
      next: (res: any) => {
        // The endpoint is deliberately unpaginated and returns a bare array.
        if (Array.isArray(res)) {
          this.userTypePermission = res.map((item: any, index: number) => ({
            ...item,
            srNo: index + 1,
          }));
        }
        this.tableLoader = false;
      },
      error: () => {
        this.tableLoader = false;
        this.toaster.error('Failed to load FSR configuration');
      },
    });
  }

  onPermissionChange(item: FSRConfigurationPermission, field: string): void {
    // ngModel has already flipped the value, so the rollback target is its inverse.
    const previousValue = !item[field];

    this._utilityService
      .updateFsrConfiguration({ id: item.id, [field]: item[field] })
      .pipe(
        catchError((error) => {
          item[field] = previousValue;
          this.handleApiFieldErrors(error, this.toaster);
          return throwError(() => error);
        })
      )
      .subscribe((response: any) => {
        if (response) {
          this.toaster.success('Permission updated successfully');
        }
      });
  }

  public searchData(event: Event): void {
    this.searchSubject.next((event.target as HTMLInputElement)?.value || '');
  }

  handleApiFieldErrors(error: HttpErrorResponse, toastr: ToastrService): void {
    if (error.error?.status && Array.isArray(error.error.status)) {
      error.error.status.forEach((msg: string) => toastr.error(msg));
      return;
    }
    if (error.error?.error && typeof error.error.error === 'string') {
      toastr.error(error.error.error);
      return;
    }
    if (error.error?.message) {
      toastr.error(error.error.message);
      return;
    }
    if (error.error?.errors) {
      Object.keys(error.error.errors).forEach((key) => {
        const message = Array.isArray(error.error.errors[key])
          ? error.error.errors[key][0]
          : error.error.errors[key];
        toastr.error(message);
      });
      return;
    }
    toastr.error('Sorry, something went wrong. Please try again.');
  }
}
